import { createTheme } from '@mui/material';
import { generateColorShades, invertColor } from '@/utils/color';
import { useBrandingTheme } from '@/hooks/stores/useBrandingTheme';
import { defaultColorPalette, spacing } from '@/constants/brandingThemes';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    success: true;
    error: true;
    outlined: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    black: true;
    white: true;
    tertiary: true;
    border: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    hover: true;
    fullHeight: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
  }
}

export const useCustomTheme = () => {
  const { brandingTheme, mode } = useBrandingTheme();

  return createTheme({
    typography: {
      fontFamily: 'MyriadPro',
      fontSize: 18,
      h1: {
        fontWeight: 'bold',
      },
      h2: {
        fontWeight: 'bold',
      },
      h3: {
        fontWeight: 'bold',
      },
      h4: {
        fontWeight: 'bold',
      },
      h5: {
        fontWeight: 'bold',
      },
      h6: {
        fontWeight: 'bold',
      },
    },
    palette: {
      mode,
      primary: {
        main: brandingTheme.primaryColor,
        contrastText: invertColor(brandingTheme.primaryColor),
        light: generateColorShades(brandingTheme.primaryColor, 500),
      },
      secondary: {
        main: brandingTheme.secondaryColor,
        contrastText: invertColor(brandingTheme.secondaryColor),
        light: generateColorShades(brandingTheme.secondaryColor, 500),
      },
      tertiary: {
        main: defaultColorPalette.tertiary,
        contrastText: invertColor(defaultColorPalette.tertiary),
        light: generateColorShades(defaultColorPalette.tertiary, 500),
      },
      error: {
        main: defaultColorPalette.error,
        contrastText: invertColor(defaultColorPalette.error),
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1000,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiButton: {
        variants: [
          {
            props: {
              variant: 'primary',
            },
            style: {
              color: invertColor(brandingTheme.primaryColor),
              backgroundColor: brandingTheme.primaryColor,
              '&:hover, &:focus': {
                backgroundColor: generateColorShades(
                  brandingTheme.primaryColor,
                  -200,
                ),
              },
              '&:disabled': {
                color: defaultColorPalette.white,
                backgroundColor: generateColorShades(
                  brandingTheme.primaryColor,
                  400,
                ),
              },
            },
          },
          {
            props: {
              variant: 'secondary',
            },
            style: {
              color: invertColor(brandingTheme.secondaryColor),
              backgroundColor: brandingTheme.secondaryColor,
              '&:hover, &:focus': {
                backgroundColor: generateColorShades(
                  brandingTheme.secondaryColor,
                  -200,
                ),
              },
              '&:disabled': {
                color: defaultColorPalette.white,
                backgroundColor: generateColorShades(
                  brandingTheme.secondaryColor,
                  400,
                ),
              },
            },
          },
          {
            props: {
              variant: 'tertiary',
            },
            style: {
              color: invertColor(defaultColorPalette.white),
              backgroundColor: defaultColorPalette.tertiary,
              '&:hover, &:focus': {
                backgroundColor: generateColorShades(
                  defaultColorPalette.tertiary,
                  100,
                ),
              },
              '&:disabled': {
                color: invertColor(defaultColorPalette.tertiary),
                backgroundColor: generateColorShades(
                  defaultColorPalette.tertiary,
                  400,
                ),
              },
            },
          },
          {
            props: {
              variant: 'outlined',
            },
            style: {
              color: brandingTheme.primaryColor,
              borderWidth: '1px',
              borderColor: brandingTheme.primaryColor,
              backgroundColor: defaultColorPalette.white,
              '&:hover, &:focus': {
                backgroundColor: brandingTheme.primaryColor,
                color: invertColor(brandingTheme.primaryColor),
              },
              '&:disabled': {
                color: defaultColorPalette.white,
                backgroundColor: generateColorShades(
                  brandingTheme.primaryColor,
                  200,
                ),
              },
            },
          },
          {
            props: {
              variant: 'text',
            },
            style: {
              padding: '0',
              border: 'none',
              '&:hover, &:focus': {
                backgroundColor: 'transparent',
              },
            },
          },
          {
            props: {
              variant: 'success',
            },
            style: {
              color: invertColor(defaultColorPalette.success),
              backgroundColor: defaultColorPalette.success,
              '&:hover, &:focus': {
                backgroundColor: generateColorShades(
                  defaultColorPalette.success,
                  800,
                ),
              },
              '&:disabled': {
                color: defaultColorPalette.white,
                backgroundColor: generateColorShades(
                  defaultColorPalette.success,
                  400,
                ),
              },
            },
          },

          {
            props: {
              variant: 'error',
            },
            style: {
              color: defaultColorPalette.white,
              backgroundColor: defaultColorPalette.error,
              '&:hover, &:focus': {
                backgroundColor: generateColorShades(
                  defaultColorPalette.error,
                  800,
                ),
              },
              '&:disabled': {
                color: defaultColorPalette.white,
                backgroundColor: generateColorShades(
                  defaultColorPalette.error,
                  300,
                ),
              },
            },
          },
        ],
      },
      MuiCard: {
        defaultProps: {
          sx: {
            padding: spacing.md,
            width: '100%',
            border: `1px solid ${defaultColorPalette.border}`,
            overflow: 'visible',
          },
        },
        variants: [
          {
            props: { variant: 'fullHeight' },
            style: {
              padding: spacing.md,
              width: '100%',
              border: `1px solid ${defaultColorPalette.border}`,
              height: '100%',
              overflow: 'visible',
            },
          },
          {
            props: { variant: 'hover' },
            style: {
              padding: spacing.md,
              width: '100%',
              border: `1px solid ${defaultColorPalette.border}`,
              cursor: 'pointer',
              ':hover': {
                backgroundColor: mode === 'light' ? '#ededed' : '#000000',
              },
            },
          },
        ],
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: '#d23627',
            font: 'inherit',
            fontSize: '14px',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: defaultColorPalette.error,
              },
            },
          },
        },
      },
    },
  });
};
